/**
 * Opal's Whiteboard logo — original artwork.
 *
 * An opal's defining quality is play-of-colour: shifting flashes of violet, teal and
 * warm gold across a soft milky base. The mark is a rounded gem with those flashes
 * arranged as overlapping facets.
 *
 * Colours come from the token layer, so a white-label rebrand recolours the mark too.
 * Nothing here is traced from or adapted from any existing logo.
 */

export interface LogoProps {
  readonly size?: number;
  readonly className?: string;
  readonly title?: string;
}

/** Square mark. Use for the favicon, small spaces, and beside the wordmark. */
export function LogoMark({ size = 48, className, title = "Opal's Whiteboard" }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role="img"
      aria-label={title}
      className={className}
    >
      <defs>
        <linearGradient id="opal-body" x1="8" y1="4" x2="40" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="var(--opal-violet-300)" />
          <stop offset="55%" stopColor="var(--opal-violet-500)" />
          <stop offset="100%" stopColor="var(--opal-violet-700)" />
        </linearGradient>
        <linearGradient id="opal-flash-a" x1="12" y1="30" x2="30" y2="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="var(--opal-teal-500)" stopOpacity="0.95" />
          <stop offset="100%" stopColor="var(--opal-teal-500)" stopOpacity="0.25" />
        </linearGradient>
        <linearGradient id="opal-flash-b" x1="20" y1="40" x2="38" y2="20" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="var(--opal-amber-500)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="var(--opal-amber-500)" stopOpacity="0.15" />
        </linearGradient>
        <clipPath id="opal-clip">
          <path d="M24 3.5c8.5 0 16 6.8 18.2 15 1.2 4.6.2 9.4-2.7 13.2C35.9 37.2 30.2 44.5 24 44.5S12.1 37.2 8.5 31.7C5.6 27.9 4.6 23.1 5.8 18.5 8 10.3 15.5 3.5 24 3.5Z" />
        </clipPath>
      </defs>

      {/* Gem body */}
      <path
        d="M24 3.5c8.5 0 16 6.8 18.2 15 1.2 4.6.2 9.4-2.7 13.2C35.9 37.2 30.2 44.5 24 44.5S12.1 37.2 8.5 31.7C5.6 27.9 4.6 23.1 5.8 18.5 8 10.3 15.5 3.5 24 3.5Z"
        fill="url(#opal-body)"
      />

      {/* Play-of-colour facets */}
      <g clipPath="url(#opal-clip)">
        <path d="M-2 28 22 4l10 10-24 24Z" fill="url(#opal-flash-a)" />
        <path d="M14 46 38 22l9 9-24 24Z" fill="url(#opal-flash-b)" />
        <path d="M28 2 40 14 30 24 18 12Z" fill="var(--neutral-0)" opacity="0.22" />
      </g>

      {/* Highlight — reads as polished stone */}
      <ellipse cx="18" cy="14.5" rx="5.5" ry="3.4" fill="var(--neutral-0)" opacity="0.4" transform="rotate(-28 18 14.5)" />
    </svg>
  );
}

/** Horizontal lockup: mark plus product name. */
export function Logo({ size = 40, className, title = "Opal's Whiteboard" }: LogoProps) {
  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3)' }}>
      <LogoMark size={size} title={title} />
      <span
        style={{
          fontFamily: 'var(--font-ui)',
          fontWeight: 700,
          fontSize: `${size * 0.46}px`,
          letterSpacing: '-0.02em',
          color: 'var(--color-text-primary)',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
    </span>
  );
}
